import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import ImageUploader from "@/components/ImageUploader";
import {
  MessageSquareQuote, Plus, Edit2, Trash2, Loader2, X, Eye, EyeOff,
  Archive, RotateCcw, ArrowUp, ArrowDown, Star,
} from "lucide-react";

const EMPTY = {
  author_ar: "", author_en: "", role_ar: "", role_en: "",
  company_ar: "", company_en: "", quote_ar: "", quote_en: "",
  image_url: "", featured: false, is_active: true, display_order: 0,
};

export default function AdminTestimonials() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => api.get("/admin/testimonials").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const visible = useMemo(() => rows.filter((r) => showArchived ? true : !r.is_archived), [rows, showArchived]);

  const save = async () => {
    if (!editing.author_en || !editing.quote_en) { toast.error("Author + quote required"); return; }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/testimonials/${editing.id}`, editing);
      else await api.post("/admin/testimonials", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const toggle = async (t) => { try { await api.put(`/admin/testimonials/${t.id}/toggle`, { is_active: !t.is_active }); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const archive = async (t) => { if (window.confirm(`Archive "${t.author_en}"?`)) { await api.post(`/admin/testimonials/${t.id}/archive`); load(); } };
  const restore = async (t) => { await api.post(`/admin/testimonials/${t.id}/restore`); load(); };
  const remove = async (t) => {
    if (!window.confirm("Delete permanently?")) return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const move = async (t, dir) => {
    const list = [...visible].sort((a, b) => a.display_order - b.display_order);
    const idx = list.findIndex((x) => x.id === t.id);
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= list.length) return;
    [list[idx], list[tgt]] = [list[tgt], list[idx]];
    // Reindex via PUT
    for (let i = 0; i < list.length; i++) {
      const it = list[i];
      if (it.display_order !== i) await api.put(`/admin/testimonials/${it.id}`, { ...it, display_order: i });
    }
    load();
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <span className="eyebrow">Content</span>
          <h1 className="mt-2 text-3xl text-navy flex items-center gap-3"><MessageSquareQuote size={26} className="text-gold" /> Testimonials</h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-navy/70 inline-flex items-center gap-2">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="tm-show-archived" /> Show archived
          </label>
          <button onClick={() => setEditing({ ...EMPTY, display_order: rows.length })} className="btn-gold" data-testid="tm-add"><Plus size={14} /> New</button>
        </div>
      </div>
      <span className="divider-gold" />

      <div className="card-elev divide-y divide-navy/10">
        {visible.length === 0 ? (
          <div className="p-8 text-center text-navy/50">No testimonials yet.</div>
        ) : visible.sort((a, b) => a.display_order - b.display_order).map((t) => (
          <div key={t.id} className="p-5 flex items-center gap-4" data-testid={`tm-row-${t.id}`}>
            <div className="flex flex-col gap-0.5">
              <button onClick={() => move(t, -1)} className="text-navy/50 hover:text-gold" data-testid={`tm-up-${t.id}`}><ArrowUp size={12} /></button>
              <button onClick={() => move(t, +1)} className="text-navy/50 hover:text-gold" data-testid={`tm-down-${t.id}`}><ArrowDown size={12} /></button>
            </div>
            {t.image_url ? (
              <img src={t.image_url} alt="" className="w-12 h-12 object-cover rounded-full border border-gold/30" />
            ) : (
              <div className="w-12 h-12 bg-navy/10 rounded-full flex items-center justify-center text-navy/40 text-xs">{(t.author_en || "?").charAt(0)}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-navy font-medium flex items-center gap-2">
                {t.author_en}
                {t.featured && <Star size={12} className="text-gold fill-gold" />}
              </div>
              <div className="text-xs text-navy/60">{[t.role_en, t.company_en].filter(Boolean).join(" · ") || "—"}</div>
            </div>
            <div className="flex items-center gap-1 whitespace-nowrap">
              {t.is_archived ? <Badge className="bg-navy/20 text-navy/70"><Archive size={11} className="inline me-1" />Archived</Badge>
               : t.is_active ? <Badge className="bg-emerald-100 text-emerald-800"><Eye size={11} className="inline me-1" />Live</Badge>
               : <Badge className="bg-amber-100 text-amber-800"><EyeOff size={11} className="inline me-1" />Hidden</Badge>}
              {!t.is_archived && <button onClick={() => toggle(t)} className="text-navy/70 p-1.5" data-testid={`tm-toggle-${t.id}`}>{t.is_active ? <EyeOff size={14} /> : <Eye size={14} />}</button>}
              <button onClick={() => setEditing({ ...EMPTY, ...t })} className="text-navy/70 p-1.5" data-testid={`tm-edit-${t.id}`}><Edit2 size={14} /></button>
              {t.is_archived ? <button onClick={() => restore(t)} className="text-emerald-700 p-1.5" data-testid={`tm-restore-${t.id}`}><RotateCcw size={14} /></button>
                             : <button onClick={() => archive(t)} className="text-amber-700 p-1.5" data-testid={`tm-archive-${t.id}`}><Archive size={14} /></button>}
              <button onClick={() => remove(t)} className="text-red-600/80 p-1.5" data-testid={`tm-del-${t.id}`}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-3xl mx-auto rounded-md border border-gold/30 p-8 my-6" onClick={(e) => e.stopPropagation()} data-testid="tm-editor">
            <div className="flex justify-between">
              <h2 className="text-2xl text-navy">{editing.id ? "Edit testimonial" : "New testimonial"}</h2>
              <button onClick={() => setEditing(null)} className="text-navy/50"><X size={18} /></button>
            </div>
            <span className="divider-gold" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input className="field-box" placeholder="Author (AR)" value={editing.author_ar} onChange={(e) => setEditing({ ...editing, author_ar: e.target.value })} data-testid="tm-author-ar" />
              <input className="field-box" placeholder="Author (EN)" value={editing.author_en} onChange={(e) => setEditing({ ...editing, author_en: e.target.value })} data-testid="tm-author-en" />
              <input className="field-box" placeholder="Position / Role (AR)" value={editing.role_ar || ""} onChange={(e) => setEditing({ ...editing, role_ar: e.target.value })} data-testid="tm-role-ar" />
              <input className="field-box" placeholder="Position / Role (EN)" value={editing.role_en || ""} onChange={(e) => setEditing({ ...editing, role_en: e.target.value })} data-testid="tm-role-en" />
              <input className="field-box" placeholder="Company (AR)" value={editing.company_ar || ""} onChange={(e) => setEditing({ ...editing, company_ar: e.target.value })} data-testid="tm-company-ar" />
              <input className="field-box" placeholder="Company (EN)" value={editing.company_en || ""} onChange={(e) => setEditing({ ...editing, company_en: e.target.value })} data-testid="tm-company-en" />
              <textarea className="field-box min-h-[100px]" placeholder="Quote (AR)" value={editing.quote_ar} onChange={(e) => setEditing({ ...editing, quote_ar: e.target.value })} data-testid="tm-quote-ar" />
              <textarea className="field-box min-h-[100px]" placeholder="Quote (EN)" value={editing.quote_en} onChange={(e) => setEditing({ ...editing, quote_en: e.target.value })} data-testid="tm-quote-en" />
              <div className="md:col-span-2">
                <label className="text-xs text-navy/70 mb-2 block">Portrait or logo</label>
                <ImageUploader value={editing.image_url || ""} folder="testimonials" testId="tm-image"
                  onChange={(url) => setEditing({ ...editing, image_url: url || "" })} />
              </div>
              <label className="flex items-center gap-2 text-navy">
                <input type="checkbox" className="w-5 h-5 accent-gold" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} data-testid="tm-featured" /> Featured
              </label>
              <label className="flex items-center gap-2 text-navy">
                <input type="checkbox" className="w-5 h-5 accent-gold" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} data-testid="tm-active" /> Show on public site
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={save} disabled={saving} className="btn-gold" data-testid="tm-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
              </button>
              <button onClick={() => setEditing(null)} className="btn-outline-navy">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
